<template>
  <v-container v-if="filter">
    <report
      :title="title"
      :filter="filter"
      :team="team"
      :manager="manager"
      v-on:refreshData="refreshData"
      v-on:rowClick="rowClick"
      :headers="headers"
      hide-anonymous
      :loading="list_loading"
      :items="items"
      :enable-business-metrics="!enps"
      :show-table="settings.isNonLitePlan"
    >
      <v-card-text class="pb-0">
        <loading-spinner v-if="trend_loading"> </loading-spinner>
        <template v-else>
          <v-row v-if="enps">
            <v-col cols="12" sm="6" xl="3" v-for="(item, i) in trend" :key="i">
              <enps-trend-card :coreCard="item"></enps-trend-card>
            </v-col>
          </v-row>
          <trend-chart
            v-else
            :loading="trend_loading"
            :height="250"
            yAxesFluid
            :label="trendTitle"
            enable-business-metrics
            :multiple-trend-data="pulse_trend_data"
          ></trend-chart>
          <v-row v-if="pulse.overview">
            <report-overall-card
              :label="valueHeaderText"
              :value="
                enps
                  ? $options.filters.no_decimal(pulse.overview.score.value)
                  : $options.filters.one_decimal(pulse.overview.score.value)
              "
              :color="$coreScores.getColor(pulse.overview.score.value)"
              :isAnonymous="isAnonymous(pulse.overview)"
              :empty-value="enps ? -100 : 0"
            >
              <template v-slot:valueExtension>
                <compare-text
                  ignore-margin
                  align-end
                  :timeframeId="filter.timeframeId"
                  :old-value="pulse.overview.score.compare"
                  :new-value="pulse.overview.score.value"
                  v-if="(enps ? -100 : 0) != pulse.overview.score.value"
                >
                </compare-text>
              </template>
            </report-overall-card>
            <report-overall-card
              v-if="isPulse"
              label="Participation"
              :value="$options.filters.no_decimal(completion) + '%'"
              :color="
                $coreScores.getColor($coreScores.reverseFormat(completion))
              "
            >
              <v-progress-linear
                class="rounded-sm my-auto"
                :value="completion"
                background-color="background"
                :color="
                  $coreScores.getColor($coreScores.reverseFormat(completion))
                "
                height="8"
              ></v-progress-linear>
            </report-overall-card>
            <template v-else>
              <report-overall-card
                label="Total members answered"
                :value="pulse.overview.userRecords.value.finished || '0'"
              >
                <template v-slot:valueExtension>
                  <div class="ml-1 mt-auto subtitle-1 lighttext--text">
                    of {{ pulse.overview.userRecords.value.sent }}
                  </div>
                </template>
                <div class="caption lighttext--text">
                  {{
                    pulse.overview.userRecords.value.completion | no_decimal
                  }}% of the people answered
                </div>
              </report-overall-card>
              <report-overall-card
                label="Total answers received"
                :value="pulse.overview.count || '0'"
              >
                <div class="caption lighttext--text">
                  # answers collected
                </div>
              </report-overall-card>
            </template>
          </v-row>
        </template>
        <v-tabs v-model="tab" v-if="settings.isNonLitePlan">
          <v-tab :class="{ 'd-none': filter.managerId }">
            Departments
          </v-tab>
          <v-tab
            :class="{
              'd-none':
                filter.managerId ||
                (pulse.overview && pulse.overview.hideManagers) ||
                !settings.showManagers
            }"
          >
            Teams
          </v-tab>
          <v-tab v-if="isPulse">
            Members
          </v-tab>
        </v-tabs>
      </v-card-text>
    </report>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import TrendChart from "@/components/reporting/TrendChart";
import CompareText from "@/components/reporting/CompareText";
import ReportOverallCard from "@/components/reporting/ReportOverallCard";
import EnpsTrendCard from "@/components/reporting/EnpsTrendCard";
import Report from "@/components/reporting/Report";
import reports from "@/helpers/mixins/reports";
export default {
  name: "PulseReport",
  props: {
    filter: { type: Object },
    team: { type: Object },
    manager: { type: Object }
  },
  components: {
    TrendChart,
    CompareText,
    ReportOverallCard,
    Report,
    EnpsTrendCard
  },
  mixins: [reports],
  data: () => ({
    tab: 0,
    list_loading: false,
    trend_loading: false
  }),
  watch: {
    tab(val, oldVal) {
      if (val != oldVal) {
        this.getList();
      }
    }
  },
  computed: {
    isAnonymous() {
      return item => {
        return (
          (item.team && item.team.hidePulseValues) ||
          (item.manager && item.manager.hidePulseValues) ||
          item.user
        );
      };
    },
    enps() {
      return this.filter.coreCardTypeId == this.$enums.coreCardTypes.ENPS;
    },
    isPulse() {
      return this.filter.coreCardTypeId == this.$enums.coreCardTypes.PULSE;
    },
    headers() {
      let items = [{ text: "Name", value: "name" }];
      if (this.isPulse) {
        items.push({
          text: "Participation",
          value: "userRecords.value.completion"
        });
      } else {
        items.push({
          text: "Members Answered",
          value: "userRecords.value.completion"
        });
        items.push({ text: "Answers Received", value: "count" });
      }
      if (this.tab != 2) {
        items.push({ text: this.valueHeaderText, value: "score.value" });
      }

      if (this.filter.timeframeId != this.$enums.timeframes.ALL_TIME) {
        if (this.tab == 2) {
          items.push({ text: "Trend", value: "userRecords.percentChange" });
        } else {
          items.push({ text: "Trend", value: "score.percentChange" });
        }
      }
      items.push({ text: "", value: "actions", sortable: false });

      return items;
    },
    completion() {
      if (
        this.pulse.overview &&
        this.pulse.overview.userRecords &&
        this.pulse.overview.userRecords.value
      ) {
        return this.pulse.overview.userRecords.value.completion;
      }
      return 0;
    },
    title() {
      switch (this.filter.coreCardTypeId) {
        case this.$enums.coreCardTypes.MISSION_VALUE:
          return `${this.t("mission.title")} & ${this.t(
            "core_values.title_short_s"
          )} Score`;
        case this.$enums.coreCardTypes.TEAM_COHESION:
          return "Team Cohesion Score";
        case this.$enums.coreCardTypes.STRATEGIC_ANCHORS:
          return "Strategic Anchor Score";
        case this.$enums.coreCardTypes.PULSE:
          return "Culture Score";
        case this.$enums.coreCardTypes.ENPS:
          return "Employee Net Promoter Score (eNPS)";
      }
      return "";
    },
    trendTitle() {
      return (
        this.trend_types
          .filter(
            x =>
              x.id == this.filter.trendId &&
              x.coreCardTypeId == this.filter.coreCardTypeId
          )
          .map(x => x.name)[0] || this.title
      );
    },
    valueHeaderText() {
      switch (this.filter.coreCardTypeId) {
        case this.$enums.coreCardTypes.MISSION_VALUE:
          return `${this.t("mission.title")} & ${this.t(
            "core_values.title_short_s"
          )} Score`;
        case this.$enums.coreCardTypes.TEAM_COHESION:
          return "Team Cohesion Score";
        case this.$enums.coreCardTypes.STRATEGIC_ANCHORS:
          return "Strategic Anchor Score";
        case this.$enums.coreCardTypes.PULSE:
          return "Culture Score";
        case this.$enums.coreCardTypes.ENPS:
          return "eNPS";
      }
      return "";
    },
    items() {
      let data = [];
      switch (this.tab) {
        case 0:
          data = this.pulse.teams;
          break;
        case 1:
          data = this.pulse.managers;
          break;
        case 2:
          data = this.pulse.members;
          break;
      }
      return data || [];
    },
    pulse_trend_data() {
      let colors = this.getRandomColors(this.multiple_trends.length);
      return this.multiple_trends.map((x, i) => ({
        label:
          this.trend_types
            .filter(y => y.id == x.trendId)
            .map(y => this.t2(y.name))[0] || "Overall",
        color: x.trendId
          ? colors[i]
          : this.$vuetify.theme.themes.light.secondary,
        trend: x.items
      }));
    },
    ...mapGetters({
      pulse: "reporting/pulse",
      trend_types: "reporting/trend_types",
      multiple_trends: "reporting/multiple_trends",
      trend: "reporting/trend",
      settings: "profile/settings"
    })
  },
  created() {
    if (this.filter != null) {
      this.update();
    }
  },
  methods: {
    rowClick(item) {
      if (item.user) {
        return;
      }
      let link = this.getLink(item);
      if (item.manager && !this.isPulse) {
        this.$root.$alert(
          "Data Hidden",
          "This data is hidden to protect the identity of the members within it"
        );
        return;
      }
      this.$router.push({ path: link });
    },
    update() {
      this.refreshData();
    },
    getList() {
      if (!this.settings.isNonLitePlan) {
        return;
      }
      this.list_loading = true;
      Promise.all([this.$store.dispatch(this.getAction(), this.filter)])
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading report."
          );
        })
        .finally(() => {
          this.list_loading = false;
        });
    },
    getAction() {
      switch (this.tab) {
        case 0:
          return "reporting/getPulseTeams";
        case 1:
          return "reporting/getPulseManagers";
        default:
          return "reporting/getPulseMembers";
      }
    },
    refreshData() {
      this.$store.dispatch("reporting/clearPulse").then(() => {
        this.list_loading = true;
        this.trend_loading = true;
        var calls = [this.$store.dispatch("reporting/getPulse", this.filter)];
        if (this.enps) {
          calls.push(
            this.$store.dispatch("reporting/getEnpsTrend", this.filter)
          );
        } else {
          calls.push(
            this.$store.dispatch("reporting/getPulseTrend", this.filter)
          );
        }
        Promise.all(calls)
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occured while loading report."
            );
          })
          .finally(() => {
            this.list_loading = false;
            this.trend_loading = false;
          })
          .then(() => {
            let newTab = this.getInitialTab(this.pulse.overview);
            if (newTab) {
              this.tab = newTab;
            }
            this.getList();
          });
      });
    }
  }
};
</script>
